// components/Courses/AddOrEditCoursePage.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Form, Row, Col, Spinner, Modal } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  Save, BookOpen, IndianRupee, Image, Clock,
  Layers, Info, Users, Link2, Eye, EyeOff, Shield, Globe2, Sparkles, Film,
} from "lucide-react";
import { PageHeader, FormActionBar, AppButton, ResetButton, AiModelPicker, Checkbox } from "../ui";
import CoursePreviewVideoField from "./CoursePreviewVideoField";
import CourseInstructorField from "./CourseInstructorField";
import { uploadCoursePreviewVideo, previewVideoUploadError } from "../../utils/coursePreviewVideoApi";
import useAiModels from "../../hooks/useAiModels";
import AiGenerateModal from "../Cms/AiGenerateModal";
import BranchAllocationField from "../Common/BranchAllocationField";
import { useBranch } from "../../contexts/BranchContext";
import { aiGenerateCourseMarketing, aiGenerateSeo } from "../../utils/cmsAdminApi";
import CommonToaster from "../Common/CommonToaster";
import apiClient from "../../utils/apiClient";
import { usePlan } from "../../contexts/PlanContext";
import CourseCatalogCategorySelect from "./CourseCatalogCategorySelect";
import CoursePaymentFields from "./CoursePaymentFields";
import { previewCoursePayments } from "../../utils/websiteCheckout";
import "../SelfPaced/_self-paced-pages.scss";
import "./Courses.scss";

const initialState = {
  name: "",
  shortCode: "",
  title: "",
  description: "",
  fees: "",
  freeDemo: "Yes",
  duration: "",
  mode: "Classroom",
  syllabus: "",
  prerequisites: "",
  highlights: "",
  maxStrength: "",
  enrollmentLink: "",
  allowSelfEnrollment: false,
  // Follows the institute's UPI QR switch; untick to opt this course out.
  qrPayment: { accept: true },
  // Registration fee: part of `fees` (paid first) or charged on top of it.
  registrationFee: { enabled: false, amount: "", includedInFee: true },
  // Pay for this course on the institute's website (also needs Settings → Payments).
  websiteCheckout: { enabled: false },
  // Branch allocation. 'all' is the default so a course created before the
  // institute had branches keeps reaching every one of them.
  branchScope: "all",
  branchIds: [],
  videoSecurity: false,
  category: "",
  // Public CMS website visibility. When false, the course is hidden from
  // the public catalog and its detail page returns 404. Doesn't affect
  // existing enrollments or LMS access — only the marketing site.
  isPublished: true,
  // When false the website shows "Contact for price" and the fee is left out
  // of the public course API as well.
  showPricePublicly: true,
  thumbnail: null,
  banner: null,
  // ── Public Website (CMS) marketing overrides ─────────────────────
  // These fields are stored under `course.cms` on the backend and only
  // power the public CMS website. They do not affect LMS / batches /
  // self-paced playback in any way.
  cms: {
    shortDescription: "",
    highlights: "",        // textarea, one bullet per line
    whatYouWillLearn: "",  // textarea, one bullet per line → "Learning Outcomes" on public site
    tags: "",              // comma-separated skill / technology chips
    showTrainerPublicly: true,
    trainer: {
      instructorId: "",     // linked Instructor profile — fills blank fields
      teamMemberId: "",     // or a website-only Team profile; never both
      photoKey: "",         // uploaded photo (storage key)
      photoPreviewUrl: "",  // display only, never saved
      name: "",
      title: "",
      bio: "",
      photoUrl: "",
      studentsTrained: "",
      coursesLed: "",
    },
    lectureCount: "",
    projectCount: "",
    studentCount: "",
    originalPrice: "",
    faq: Array.from({ length: 10 }, () => ({ question: '', answer: '' })),
  },
};

/* ── CMS field helpers ─────────────────────────────────────── */
const linesToArray = (s) =>
  String(s || "")
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);
const arrayToLines = (a) => (Array.isArray(a) ? a.join("\n") : "");

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ indent: "-1" }, { indent: "+1" }],
    [{ color: [] }, { background: [] }],
    ["link"],
    ["clean"],
  ],
};

const quillFormats = [
  "header", "bold", "italic", "underline", "strike",
  "list", "indent", "color", "background", "link",
];

/* ── Section wrapper with icon ───────────────────────────── */
const Section = ({ icon, title, subtitle, children, className = "" }) => (
  <div className={`sp-form-section ${className}`}>
    <div className="sp-form-section-header">
      <div className="sp-form-section-icon">{icon}</div>
      <div>
        <h6 className="sp-form-section-title">{title}</h6>
        {subtitle && <p className="sp-form-section-subtitle">{subtitle}</p>}
      </div>
    </div>
    <div className="sp-form-section-body">{children}</div>
  </div>
);

/* ── Field wrapper ───────────────────────────────────────── */
const Field = ({ label, required, hint, children }) => (
  <div className="sp-field">
    <label className="sp-field-label">
      {label} {required && <span style={{ color: "#e11d48" }}>*</span>}
    </label>
    {children}
    {hint && <span className="sp-field-hint">{hint}</span>}
  </div>
);

const AddOrEditCoursePage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  // A branch-restricted admin may edit the course but not its reach — the API
  // refuses that change, so the control is shown read-only rather than letting
  // them make an edit that comes back 403.
  const { restricted: branchRestricted } = useBranch();
  const { hasPlanFeature } = usePlan();
  const hasWebsite = hasPlanFeature("cmsWebsite");
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(!!courseId);
  const [saving, setSaving] = useState(false);
  // Kept out of `form`: a saved course's preview is already live, so Reset and
  // Cancel have nothing to undo. A new course's file waits for the course.
  const [previewVideo, setPreviewVideo] = useState(null);
  const [pendingPreview, setPendingPreview] = useState(null);
  const [videoProgress, setVideoProgress] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const [previewImg, setPreviewImg] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [bannerDragActive, setBannerDragActive] = useState(false);
  const [previewModal, setPreviewModal] = useState({ show: false, title: "", html: "" });
  const fileRef = useRef(null);
  const bannerRef = useRef(null);

  // Pristine snapshot for the Reset action (captured once after initial load).
  const pristineRef = useRef({ form: initialState, previewImg: null, bannerPreview: null });
  const pristineCaptured = useRef(false);

  // Fetch course for edit
  useEffect(() => {
    if (courseId) {
      (async () => {
        try {
          const { data } = await apiClient.get(`/courses/${courseId}`);
          const c = data.course || data;
          setForm({
            ...initialState,
            name: c.name || "",
            shortCode: c.shortCode || "",
            title: c.title || "",
            description: c.description || "",
            fees: c.fees ?? "",
            freeDemo: c.freeDemo || "Yes",
            duration: c.duration || "",
            mode: c.mode || "Classroom",
            syllabus: c.syllabus || "",
            prerequisites: c.prerequisites || "",
            highlights: c.highlights || "",
            maxStrength: c.maxStrength ?? "",
            enrollmentLink: c.enrollmentLink || "",
            allowSelfEnrollment: c.allowSelfEnrollment || false,
            qrPayment: { accept: c.qrPayment?.accept !== false },
            registrationFee: {
              enabled: c.registrationFee?.enabled === true,
              amount: c.registrationFee?.amount ? String(c.registrationFee.amount) : "",
              includedInFee: c.registrationFee?.includedInFee !== false,
            },
            websiteCheckout: { enabled: c.websiteCheckout?.enabled === true },
            branchScope: c.branchScope === "branches" ? "branches" : "all",
            branchIds: Array.isArray(c.branchIds) ? c.branchIds.map((b) => String(b?._id || b)) : [],
            videoSecurity: c.videoSecurity || false,
            category: c.category || "",
            // BE default flips to true for new docs; treat undefined as visible
            // so legacy batch courses without the field stay visible after backfill.
            isPublished: c.isPublished !== false,
            showPricePublicly: c.showPricePublicly !== false,
            thumbnail: null,
            banner: null,
            cms: {
              shortDescription: c.cms?.shortDescription || "",
              highlights: arrayToLines(c.cms?.highlights),
              whatYouWillLearn: arrayToLines(c.cms?.whatYouWillLearn),
              tags: Array.isArray(c.cms?.tags) ? c.cms.tags.join(", ") : "",
              showTrainerPublicly: c.cms?.showTrainerPublicly !== false,
              trainer: {
                instructorId: c.cms?.trainer?.instructorId ? String(c.cms.trainer.instructorId) : "",
                teamMemberId: c.cms?.trainer?.teamMemberId ? String(c.cms.trainer.teamMemberId) : "",
                photoKey: c.cms?.trainer?.photoKey || "",
                photoPreviewUrl: c.cms?.trainer?.photoPreviewUrl || "",
                name: c.cms?.trainer?.name || "",
                title: c.cms?.trainer?.title || "",
                bio: c.cms?.trainer?.bio || "",
                photoUrl: c.cms?.trainer?.photoUrl || "",
                studentsTrained: c.cms?.trainer?.studentsTrained ?? "",
                coursesLed: c.cms?.trainer?.coursesLed ?? "",
              },
              lectureCount: c.cms?.lectureCount ?? "",
              projectCount: c.cms?.projectCount ?? "",
              studentCount: c.cms?.studentCount ?? "",
              originalPrice: c.cms?.originalPrice ?? "",
              faq: Array.isArray(c.cms?.faq) && c.cms.faq.length
                ? c.cms.faq.slice(0, 10).map((f) => ({ question: f.question || '', answer: f.answer || '' }))
                : Array.from({ length: 5 }, () => ({ question: '', answer: '' })),
              seo: {
                metaTitle: c.cms?.seo?.metaTitle || "",
                metaDescription: c.cms?.seo?.metaDescription || "",
                metaKeywords: Array.isArray(c.cms?.seo?.metaKeywords) ? c.cms.seo.metaKeywords.join(", ") : "",
                ogImage: c.cms?.seo?.ogImage || "",
                noindex: !!c.cms?.seo?.noindex,
              },
            },
          });
          if (c.thumbnailUrl || c.thumbnail) setPreviewImg(c.thumbnailUrl || c.thumbnail);
          if (c.bannerUrl || c.banner) setBannerPreview(c.bannerUrl || c.banner);
          setPreviewVideo(c.previewVideo || null);
        } catch {
          setToast({ show: true, message: "Failed to load course.", type: "error" });
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setForm(initialState);
      setPreviewImg(null);
      setBannerPreview(null);
    }
    // eslint-disable-next-line
  }, [courseId]);

  // Snapshot the pristine form once the initial (add / edit-load) state settles.
  useEffect(() => {
    if (!loading && !pristineCaptured.current) {
      pristineRef.current = { form, previewImg, bannerPreview };
      pristineCaptured.current = true;
    }
  }, [loading, form, previewImg, bannerPreview]);

  const set = (name, value) => setForm((p) => ({ ...p, [name]: value }));

  const handleReset = () => {
    const snap = pristineRef.current;
    setForm(snap.form);
    setPreviewImg(snap.previewImg);
    setBannerPreview(snap.bannerPreview);
    setPendingPreview(null);
  };

  const handleFile = (file) => {
    if (!file) return;
    setForm((p) => ({ ...p, thumbnail: file }));
    setPreviewImg(URL.createObjectURL(file));
  };

  const handleBannerFile = (file) => {
    if (!file) return;
    setForm((p) => ({ ...p, banner: file }));
    setBannerPreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.registrationFee?.enabled) {
      const fees = previewCoursePayments({ fees: form.fees, registrationFee: form.registrationFee });
      if (fees.error) {
        setToast({ show: true, message: fees.error, type: "error" });
        return;
      }
    }
    setSaving(true);
    try {
      const payload = { ...form };
      const thumbnailFile = payload.thumbnail;
      const bannerFile = payload.banner;
      delete payload.thumbnail;
      delete payload.banner;

      // Clean empty optional fields
      ["shortCode", "title", "duration", "enrollmentLink"].forEach(
        (k) => { if (!payload[k]) delete payload[k]; }
      );

      payload.category = (form.category ?? "").trim();

      // ── Normalize CMS subdoc: textareas → arrays, blanks → null ──
      const cmsRaw = form.cms || {};
      const numOrNull = (v) =>
        v === "" || v === null || v === undefined || isNaN(Number(v))
          ? null
          : Number(v);
      payload.cms = {
        shortDescription: cmsRaw.shortDescription || "",
        highlights: linesToArray(cmsRaw.highlights),
        whatYouWillLearn: linesToArray(cmsRaw.whatYouWillLearn),
        // Chips are a fixed-width slot in the public themes, so keep them short
        // and few — anything longer reads as a broken sentence, not a skill.
        tags: String(cmsRaw.tags || "")
          .split(",").map((t) => t.trim()).filter(Boolean).slice(0, 12),
        showTrainerPublicly: cmsRaw.showTrainerPublicly !== false,
        trainer: {
          // null, not "": an empty string cannot be cast to an ObjectId.
          instructorId: cmsRaw.trainer?.instructorId || null,
          teamMemberId: cmsRaw.trainer?.teamMemberId || null,
          photoKey: cmsRaw.trainer?.photoKey || "",
          name: (cmsRaw.trainer?.name || "").trim(),
          title: (cmsRaw.trainer?.title || "").trim(),
          bio: (cmsRaw.trainer?.bio || "").trim(),
          photoUrl: (cmsRaw.trainer?.photoUrl || "").trim(),
          studentsTrained: numOrNull(cmsRaw.trainer?.studentsTrained),
          coursesLed: numOrNull(cmsRaw.trainer?.coursesLed),
        },
        lectureCount: numOrNull(cmsRaw.lectureCount),
        projectCount: numOrNull(cmsRaw.projectCount),
        studentCount: numOrNull(cmsRaw.studentCount),
        originalPrice: numOrNull(cmsRaw.originalPrice),
        faq: (Array.isArray(cmsRaw.faq) ? cmsRaw.faq : [])
          .map((f) => ({ question: (f.question || '').trim(), answer: (f.answer || '').trim() }))
          .filter((f) => f.question || f.answer),
        seo: {
          metaTitle: (cmsRaw.seo?.metaTitle || "").trim(),
          metaDescription: (cmsRaw.seo?.metaDescription || "").trim(),
          metaKeywords: String(cmsRaw.seo?.metaKeywords || "")
            .split(",").map((s) => s.trim()).filter(Boolean),
          ogImage: (cmsRaw.seo?.ogImage || "").trim(),
          noindex: !!cmsRaw.seo?.noindex,
        },
      };

      let savedCourseId = courseId;
      if (courseId) {
        await apiClient.put(`/courses/${courseId}`, payload);
      } else {
        const res = await apiClient.post("/courses/add", payload);
        savedCourseId = res.data?.course?._id || res.data?._id;
      }

      // Upload thumbnail via server-side upload if a new file was selected
      if (thumbnailFile && savedCourseId) {
        const formData = new FormData();
        formData.append("file", thumbnailFile);
        await apiClient.post(`/courses/${savedCourseId}/thumbnail`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // Upload banner via server-side upload if a new file was selected
      if (bannerFile && savedCourseId) {
        const formData = new FormData();
        formData.append("file", bannerFile);
        await apiClient.post(`/courses/${savedCourseId}/banner`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      // A new course's preview video could only be uploaded once the course
      // existed. The course is saved either way; a failed video is retried
      // from the editor, so say so rather than report the whole save failed.
      if (pendingPreview && savedCourseId) {
        try {
          setVideoProgress(0);
          await uploadCoursePreviewVideo(savedCourseId, pendingPreview, { onProgress: setVideoProgress });
        } catch (videoErr) {
          setToast({
            show: true,
            type: "error",
            message: `Course saved, but the preview video did not upload: ${previewVideoUploadError(videoErr)} Edit the course to try again.`,
          });
          setTimeout(() => navigate(`/courses/${savedCourseId}`), 3500);
          return;
        }
      }

      setToast({ show: true, message: courseId ? "Course updated successfully!" : "Course added successfully!", type: "success" });
      // Return to the course's detail page (fall back to the list if we somehow
      // don't have an id, e.g. an add response that didn't return one).
      setTimeout(() => navigate(savedCourseId ? `/courses/${savedCourseId}` : "/courses"), 1200);
    } catch (err) {
      setToast({
        show: true,
        message: err?.response?.data?.message || "Failed to save course.",
        type: "error",
      });
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="sp-loading">
        <Spinner animation="border" variant="secondary" />
      </div>
    );
  }

  const isEdit = !!courseId;

  return (
    <div className="sp-form-page has-form-actionbar">
      {/* Page header (portaled into the top nav) */}
      <PageHeader
        icon={BookOpen}
        title={isEdit ? "Edit Course" : "Add New Course"}
        description="Batch Course"
        backButton
        onBack={() => navigate(isEdit ? `/courses/${courseId}` : "/courses")}
      />

      {/* Form Body */}
      <Form id="batchCourseForm" onSubmit={handleSubmit}>
        <Row className="g-4 mt-0">
          {/* Left Column */}
          <Col lg={7} xl={8}>
            {/* Basic Details */}
            <Section icon={<BookOpen size={18} />} title="Basic Details" subtitle="The essential info students see first">
              <Row className="g-3">
                <Col md={7}>
                  <Field label="Course Name" required>
                    <Form.Control
                      name="name"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      required
                      placeholder="e.g., Full Stack Web Development"
                    />
                  </Field>
                </Col>
                <Col md={5}>
                  <Field label="Short Code" hint="Used as batch name prefix">
                    <Form.Control
                      className="sp-mono"
                      name="shortCode"
                      value={form.shortCode}
                      onChange={(e) => set("shortCode", e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ""))}
                      placeholder="e.g., JAVA_FD"
                      maxLength={12}
                    />
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Course Title / Tagline">
                    <Form.Control
                      name="title"
                      value={form.title}
                      onChange={(e) => set("title", e.target.value)}
                      placeholder="A short, catchy title shown under the name"
                    />
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Description" hint="Describe the course outcomes and target audience — use headings, bullets, and formatting">
                    <div className="cr-editor-wrap">
                      <ReactQuill theme="snow" value={form.description} onChange={(v) => set("description", v)} modules={quillModules} formats={quillFormats} placeholder="What will students learn? Who is this course for?" />
                      <button type="button" className="cr-preview-btn" onClick={() => setPreviewModal({ show: true, title: "Description", html: form.description })}><Eye size={14} /> Preview</button>
                    </div>
                  </Field>
                </Col>
                <Col md={6}>
                  <Field
                    label="Public website category"
                    hint="Optional. Groups this course in filters on your marketing site and matches self-paced categories."
                  >
                    <CourseCatalogCategorySelect
                      value={form.category}
                      onChange={(v) => set("category", v)}
                    />
                  </Field>
                </Col>
              </Row>
            </Section>

            {/* Pricing & Schedule */}
            <Section icon={<IndianRupee size={18} />} title="Pricing & Schedule" subtitle="Fees, registration fee, website payments, demo availability and duration">
              <Row className="g-3">
                <Col md={4}>
                  <Field label="Fees (INR)">
                    <div className="sp-input-icon-wrap">
                      <IndianRupee size={15} className="sp-input-icon" />
                      <Form.Control
                        type="number"
                        min={0}
                        className="sp-input-with-icon"
                        name="fees"
                        value={form.fees}
                        onChange={(e) => set("fees", e.target.value)}
                        placeholder="e.g., 14999"
                      />
                    </div>
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Free Demo">
                    <Form.Select name="freeDemo" value={form.freeDemo} onChange={(e) => set("freeDemo", e.target.value)}>
                      <option>Yes</option>
                      <option>No</option>
                    </Form.Select>
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Duration">
                    <div className="sp-input-icon-wrap">
                      <Clock size={15} className="sp-input-icon" />
                      <Form.Control
                        className="sp-input-with-icon"
                        name="duration"
                        value={form.duration}
                        onChange={(e) => set("duration", e.target.value)}
                        placeholder="e.g., 4 months"
                      />
                    </div>
                  </Field>
                </Col>
              </Row>
              {hasWebsite && (
                <Checkbox
                  className="mt-3"
                  checked={form.showPricePublicly !== false}
                  onChange={(e) => set("showPricePublicly", e.target.checked)}
                  label="Show the fee on your website"
                  description={form.showPricePublicly !== false
                    ? "Visitors see the fee on this course's page and on its course card."
                    : "Your website shows “Contact for price”. The fee is also left out of the website's API, so it cannot be read from the page source either."}
                />
              )}
              <div className="mt-3">
                <CoursePaymentFields
                  fees={form.fees}
                  registrationFee={form.registrationFee}
                  websiteCheckout={form.websiteCheckout}
                  showPricePublicly={form.showPricePublicly}
                  hasWebsite={hasWebsite}
                  onChange={(patch) => setForm((p) => ({ ...p, ...patch }))}
                />
              </div>
            </Section>

            {/* Batch Settings */}
            <Section icon={<Users size={18} />} title="Batch Settings" subtitle="Mode, capacity, and enrollment options">
              <Row className="g-3">
                <Col md={4}>
                  <Field label="Mode">
                    <Form.Select name="mode" value={form.mode} onChange={(e) => set("mode", e.target.value)}>
                      <option>Classroom</option>
                      <option>Online</option>
                      <option>Both</option>
                    </Form.Select>
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Max Strength" hint="Max students per batch">
                    <Form.Control
                      type="number"
                      min={1}
                      name="maxStrength"
                      value={form.maxStrength}
                      onChange={(e) => set("maxStrength", e.target.value)}
                      placeholder="e.g., 40"
                    />
                  </Field>
                </Col>
                <Col md={4}>
                  <Field label="Enrollment Link">
                    <div className="sp-input-icon-wrap">
                      <Link2 size={15} className="sp-input-icon" />
                      <Form.Control
                        className="sp-input-with-icon"
                        name="enrollmentLink"
                        value={form.enrollmentLink}
                        onChange={(e) => set("enrollmentLink", e.target.value)}
                        placeholder="Paste link (optional)"
                      />
                    </div>
                  </Field>
                </Col>
                <Col xs={12}>
                  <Form.Check
                    type="switch"
                    id="allowSelfEnrollment"
                    label="Allow Student Self-Enrollment"
                    checked={!!form.allowSelfEnrollment}
                    onChange={(e) => set("allowSelfEnrollment", e.target.checked)}
                  />
                  {form.allowSelfEnrollment && (
                    <Checkbox
                      className="mt-2"
                      checked={form.qrPayment?.accept !== false}
                      onChange={(e) => set("qrPayment", { accept: e.target.checked })}
                      label="Accept UPI QR payments"
                      description="When UPI QR is on in Settings → Payments, students can pay your QR and upload the screenshot to enrol — and get access only after you approve it in Payment approvals. Untick to keep this course to online payment or a request to join."
                    />
                  )}
                </Col>
                <Col xs={12}>
                  {/* Renders nothing for a single-branch institute. */}
                  <BranchAllocationField
                    scope={form.branchScope}
                    branchIds={form.branchIds}
                    disabled={branchRestricted}
                    onChange={({ branchScope, branchIds }) => {
                      set("branchScope", branchScope);
                      set("branchIds", branchIds);
                    }}
                  />
                </Col>
              </Row>
            </Section>

            {/* Video Security */}
            <VideoSecurityToggle form={form} set={set} />

            {/* Syllabus & Other Info */}
            <Section icon={<Layers size={18} />} title="Additional Details" subtitle="Enrich your course page — use headings, bullet points, and formatting" className="mb-4">
              <Row className="g-3">
                <Col xs={12}>
                  <Field label="Syllabus Outline">
                    <div className="cr-editor-wrap">
                      <ReactQuill theme="snow" value={form.syllabus} onChange={(v) => set("syllabus", v)} modules={quillModules} formats={quillFormats} placeholder="Main syllabus points, modules, etc." />
                      <button type="button" className="cr-preview-btn" onClick={() => setPreviewModal({ show: true, title: "Syllabus Outline", html: form.syllabus })}><Eye size={14} /> Preview</button>
                    </div>
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Prerequisites">
                    <div className="cr-editor-wrap">
                      <ReactQuill theme="snow" value={form.prerequisites} onChange={(v) => set("prerequisites", v)} modules={quillModules} formats={quillFormats} placeholder="e.g., Basic programming knowledge" />
                      <button type="button" className="cr-preview-btn" onClick={() => setPreviewModal({ show: true, title: "Prerequisites", html: form.prerequisites })}><Eye size={14} /> Preview</button>
                    </div>
                  </Field>
                </Col>
                <Col xs={12}>
                  <Field label="Key Highlights">
                    <div className="cr-editor-wrap">
                      <ReactQuill theme="snow" value={form.highlights} onChange={(v) => set("highlights", v)} modules={quillModules} formats={quillFormats} placeholder="100% placement, Live projects, Certificate" />
                      <button type="button" className="cr-preview-btn" onClick={() => setPreviewModal({ show: true, title: "Key Highlights", html: form.highlights })}><Eye size={14} /> Preview</button>
                    </div>
                  </Field>
                </Col>
              </Row>
            </Section>

            {/* Public Website (CMS) — only visible when CMS plan feature is on */}
            <CmsMarketingSection form={form} setForm={setForm} />
          </Col>

          {/* Right Column */}
          <Col lg={5} xl={4}>
            {/* Public website visibility — gates appearance on vc_cms */}
            <Section
              icon={form.isPublished ? <Eye size={18} /> : <EyeOff size={18} />}
              title="Visibility"
              className="mb-4"
            >
              <div
                className="sp-visibility-card"
                role="button"
                tabIndex={0}
                onClick={() => set("isPublished", !form.isPublished)}
                onKeyDown={(e) => {
                  if (e.key === " " || e.key === "Enter") {
                    e.preventDefault();
                    set("isPublished", !form.isPublished);
                  }
                }}
              >
                <div className={`sp-visibility-dot ${form.isPublished ? "live" : ""}`} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--text-primary)" }}>
                    {form.isPublished ? "Published" : "Hidden"}
                  </div>
                  <small style={{ color: "hsl(var(--muted-foreground))" }}>
                    {form.isPublished
                      ? "Visible on the public website catalog & course page"
                      : "Hidden from the public catalog — existing enrollments are unaffected"}
                  </small>
                </div>
                <Form.Check
                  type="switch"
                  id="batch-course-isPublished"
                  checked={!!form.isPublished}
                  onChange={(e) => set("isPublished", e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  className="ms-2"
                />
              </div>
            </Section>

            {/* Thumbnail dropzone */}
            <Section icon={<Image size={18} />} title="Course Thumbnail" subtitle="Recommended: 1280 × 720 px">
              <div
                className={`sp-dropzone ${dragActive ? "active" : ""} ${previewImg ? "has-image" : ""}`}
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files[0]); }}
              >
                {previewImg ? (
                  <img src={previewImg} alt="thumbnail" className="sp-dropzone-preview" />
                ) : (
                  <div className="sp-dropzone-placeholder">
                    <Image size={32} strokeWidth={1.5} />
                    <span>Drop image here or click to browse</span>
                    <small>JPG, PNG, WebP up to 5 MB</small>
                  </div>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleFile(e.target.files[0])}
                />
              </div>
              {previewImg && (
                <button
                  type="button"
                  className="sp-remove-img"
                  onClick={(e) => { e.stopPropagation(); setPreviewImg(null); setForm((p) => ({ ...p, thumbnail: null })); }}
                >
                  Remove image
                </button>
              )}
            </Section>

            {/* Banner dropzone */}
            <Section icon={<Image size={18} />} title="Course Banner" subtitle="Recommended: 1920 × 480 px (wide banner for course detail page)">
              <div
                className={`sp-dropzone ${bannerDragActive ? "active" : ""} ${bannerPreview ? "has-image" : ""}`}
                onClick={() => bannerRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setBannerDragActive(true); }}
                onDragLeave={() => setBannerDragActive(false)}
                onDrop={(e) => { e.preventDefault(); setBannerDragActive(false); handleBannerFile(e.dataTransfer.files[0]); }}
              >
                {bannerPreview ? (
                  <img src={bannerPreview} alt="banner" className="sp-dropzone-preview" />
                ) : (
                  <div className="sp-dropzone-placeholder">
                    <Image size={32} strokeWidth={1.5} />
                    <span>Drop banner image here or click to browse</span>
                    <small>JPG, PNG, WebP up to 5 MB</small>
                  </div>
                )}
                <input
                  ref={bannerRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleBannerFile(e.target.files[0])}
                />
              </div>
              {bannerPreview && (
                <button
                  type="button"
                  className="sp-remove-img"
                  onClick={(e) => { e.stopPropagation(); setBannerPreview(null); setForm((p) => ({ ...p, banner: null })); }}
                >
                  Remove banner
                </button>
              )}
            </Section>

            {hasWebsite && (
              <Section
                icon={<Film size={18} />}
                title="Preview Video"
                subtitle="A short, free clip anyone can watch on this course's website page"
              >
                <CoursePreviewVideoField
                  courseId={courseId}
                  video={previewVideo}
                  onVideoChange={setPreviewVideo}
                  pendingFile={pendingPreview}
                  onPendingFileChange={setPendingPreview}
                  disabled={saving}
                />
              </Section>
            )}

            {/* Quick Tips */}
            <div className="sp-tips-card">
              <div className="sp-tips-header"><Info size={15} /> Quick Tips</div>
              <ul className="sp-tips-list">
                <li>Use a compelling thumbnail — it's the first thing students see</li>
                <li>Set a short code to auto-prefix batch names</li>
                <li>Enable self-enrollment to let students apply directly</li>
                <li>Add batches after saving the course</li>
              </ul>
            </div>
          </Col>
        </Row>
      </Form>

      {/* ── Fixed bottom action bar ── */}
      <FormActionBar>
        <AppButton
          variant="outline"
          type="button"
          onClick={() => navigate(isEdit ? `/courses/${courseId}` : "/courses")}
        >
          Cancel
        </AppButton>
        <ResetButton onReset={handleReset} />
        <AppButton type="submit" form="batchCourseForm" disabled={saving}>
          {saving ? (
            <>
              <Spinner size="sm" animation="border" className="me-2" />
              {videoProgress === null ? "Saving…" : `Uploading video… ${videoProgress}%`}
            </>
          ) : (
            <><Save size={15} /> {isEdit ? "Update Course" : "Save Course"}</>
          )}
        </AppButton>
      </FormActionBar>

      {/* Rich-text Preview Modal */}
      <Modal
        show={previewModal.show}
        onHide={() => setPreviewModal({ show: false, title: "", html: "" })}
        centered
        size="lg"
        className="cr-preview-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>{previewModal.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="cr-preview-body ql-editor" dangerouslySetInnerHTML={{ __html: previewModal.html || '<p style="color:var(--text-secondary,#999)">Nothing to preview yet.</p>' }} />
        </Modal.Body>
      </Modal>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

/* ── Video Security Toggle (reused in course forms) ───────── */
const VideoSecurityToggle = ({ form, set }) => {
  const { hasPlanFeature } = usePlan();
  const planAllows = hasPlanFeature('secureVideoPlayer') && hasPlanFeature('videoSecurityEnabled');

  return (
    <Section icon={<Shield size={18} />} title="Video Security" subtitle="Require the desktop app for students to watch videos in this course">
      <div
        className="sp-visibility-card"
        style={{ cursor: planAllows ? 'pointer' : 'default', opacity: planAllows ? 1 : 0.55 }}
        onClick={() => planAllows && set("videoSecurity", !form.videoSecurity)}
        role="button"
        tabIndex={0}
      >
        <div className={`sp-visibility-dot ${planAllows && form.videoSecurity ? 'live' : ''}`} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
            {planAllows && form.videoSecurity ? 'Enabled' : 'Disabled'}
          </div>
          <small style={{ color: 'hsl(var(--muted-foreground))' }}>
            {!planAllows
              ? (!hasPlanFeature('secureVideoPlayer')
                  ? 'Your plan does not include video security. Upgrade to enable.'
                  : 'Video security is not enabled in institute settings. Enable it in Settings → Modules.')
              : form.videoSecurity
                ? 'Students must use the Desktop App to watch videos in this course.'
                : 'Students can watch videos in the browser — no desktop app required.'}
          </small>
        </div>
        <Form.Check
          type="switch"
          id="videoSecurity"
          checked={planAllows ? !!form.videoSecurity : false}
          onChange={() => planAllows && set("videoSecurity", !form.videoSecurity)}
          disabled={!planAllows}
          className="ms-2"
        />
      </div>
      <small style={{ display: 'block', marginTop: '0.5rem', fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>
        Download prevention and watermarks are always active regardless of this setting.
      </small>
    </Section>
  );
};

/* ── Public Website (CMS) — marketing overrides ───────────── */
const CmsMarketingSection = ({ form, setForm }) => {
  const { hasPlanFeature } = usePlan();
  const { courseId } = useParams();
  const [aiOpen, setAiOpen] = useState(false);
  const [seoAiBusy, setSeoAiBusy] = useState(false);
  // Hooks must run before the early return below, so this is unconditional and
  // gated by its `active` flag instead — `aiAvailable` also depends on the form,
  // which is not known yet here.
  const seoAi = useAiModels('/cms/ai/models', hasPlanFeature('aiCmsContent'));
  if (!hasPlanFeature('cmsWebsite')) return null;

  const aiAvailable = hasPlanFeature('aiCmsContent') && (!!courseId || !!(form.name || form.title));
  const cms = form.cms || {};
  const setCms = (key, value) =>
    setForm((p) => ({ ...p, cms: { ...(p.cms || {}), [key]: value } }));
  const setTrainer = (patch) =>
    setForm((p) => ({
      ...p,
      cms: {
        ...(p.cms || {}),
        trainer: { ...((p.cms || {}).trainer || {}), ...patch },
      },
    }));

  // `ai` is the admin's model pick ({ model, reasoningEffort? }) from the
  // modal's AiModelPicker — forward it or the server silently uses its default.
  const runAi = async ({ prompt, tone, ...ai }, { regenerate }) => {
    return aiGenerateCourseMarketing({
      courseId: courseId || undefined,
      courseName: form.title || form.name || 'this course',
      courseDescription: form.description || '',
      durationDays: form.durationDays || form.totalDays || undefined,
      tone,
      prompt,
      regenerate,
      ...ai,
    });
  };

  const applyAi = (data) => {
    // Build FAQ array — ensure we have 10 slots (pad with empty if AI returns fewer)
    const aiFaq = Array.isArray(data.faq) ? data.faq : [];
    const mergedFaq = Array.from({ length: 10 }, (_, i) => ({
      question: aiFaq[i]?.question || '',
      answer: aiFaq[i]?.answer || '',
    }));

    setForm((p) => ({
      ...p,
      cms: {
        ...(p.cms || {}),
        shortDescription: data.shortDescription || p.cms?.shortDescription || '',
        highlights: Array.isArray(data.highlights)
          ? data.highlights.join('\n')
          : (data.highlights || p.cms?.highlights || ''),
        whatYouWillLearn: Array.isArray(data.whatYouWillLearn)
          ? data.whatYouWillLearn.join('\n')
          : (data.whatYouWillLearn || p.cms?.whatYouWillLearn || ''),
        trainer: {
          ...(p.cms?.trainer || {}),
          bio: p.cms?.trainer?.bio || data.trainerBio || '',
        },
        faq: mergedFaq,
      },
    }));
  };

  return (
    <Section
      icon={<Globe2 size={18} />}
      title="Public Website (CMS)"
      subtitle="Optional marketing overrides shown on the public course detail page. Does not affect LMS, batches, or self-paced playback."
      className="mb-4"
    >
      {aiAvailable && (
        <div className="cms-ai-bar">
          <AppButton variant="secondary" type="button" onClick={() => setAiOpen(true)}>
            <Sparkles size={14} /> Generate Marketing Copy with AI
          </AppButton>
          <small>AI fills description, highlights, learning outcomes, and FAQs — you stay in control.</small>
        </div>
      )}
      <Row className="g-3">
        <Col xs={12}>
          <Field
            label="Short Description"
            hint="One-line summary shown on course cards (max ~160 chars)"
          >
            <Form.Control
              as="textarea"
              rows={2}
              value={cms.shortDescription}
              onChange={(e) => setCms("shortDescription", e.target.value)}
              placeholder="e.g., Master modern full-stack development with hands-on projects and live mentor support."
              maxLength={300}
            />
          </Field>
        </Col>

        <Col md={6}>
          <Field label="Highlights" hint="One bullet per line">
            <Form.Control
              as="textarea"
              rows={5}
              value={cms.highlights}
              onChange={(e) => setCms("highlights", e.target.value)}
              placeholder={"Industry-grade curriculum\nReal production-style projects\n1-on-1 mentor support"}
            />
          </Field>
        </Col>
        <Col md={6}>
          <Field label="Learning Outcomes" hint="One outcome per line (shown on the public course page)">
            <Form.Control
              as="textarea"
              rows={5}
              value={cms.whatYouWillLearn}
              onChange={(e) => setCms("whatYouWillLearn", e.target.value)}
              placeholder={"Build production-ready APIs\nDeploy to the cloud with CI/CD\nWrite testable, clean code"}
            />
          </Field>
        </Col>

        <Col xs={12}>
          <Field
            label="Skills & Technologies"
            hint="Comma separated, up to 12. Shown as chips on course cards and the course page — keep each to one or two words. Leave blank to show no chips."
          >
            <Form.Control
              type="text"
              value={cms.tags}
              onChange={(e) => setCms("tags", e.target.value)}
              placeholder="e.g., Azure, DevOps, Terraform, Kubernetes, CI/CD"
            />
          </Field>
        </Col>

        <Col md={3}>
          <Field label="Original Price (₹)" hint="Strike-through marketing price">
            <Form.Control
              type="number"
              min={0}
              value={cms.originalPrice}
              onChange={(e) => setCms("originalPrice", e.target.value)}
              placeholder="e.g., 24999"
            />
          </Field>
        </Col>
        <Col md={3}>
          <Field label="Lectures (count)">
            <Form.Control
              type="number"
              min={0}
              value={cms.lectureCount}
              onChange={(e) => setCms("lectureCount", e.target.value)}
              placeholder="e.g., 120"
            />
          </Field>
        </Col>
        <Col md={3}>
          <Field label="Projects (count)">
            <Form.Control
              type="number"
              min={0}
              value={cms.projectCount}
              onChange={(e) => setCms("projectCount", e.target.value)}
              placeholder="e.g., 8"
            />
          </Field>
        </Col>
        <Col md={3}>
          <Field label="Students (count)" hint="Marketing display only">
            <Form.Control
              type="number"
              min={0}
              value={cms.studentCount}
              onChange={(e) => setCms("studentCount", e.target.value)}
              placeholder="e.g., 1500"
            />
          </Field>
        </Col>

        {/* Instructor card — typed, or linked to an instructor profile */}
        <Col xs={12}>
          <CourseInstructorField
            courseId={courseId}
            show={cms.showTrainerPublicly !== false}
            onShowChange={(value) => setCms("showTrainerPublicly", value)}
            trainer={cms.trainer || {}}
            onTrainerChange={setTrainer}
          />
        </Col>
      </Row>

      {/* ── Per-course FAQ ──────────────────────────────────────────────── */}
      <div className="cms-trainer-block" style={{ marginTop: '1rem' }}>
        <h6 className="cms-trainer-block-title">FAQs (course detail page)</h6>
        <small style={{ display: 'block', marginBottom: '0.75rem', color: 'var(--text-tertiary)' }}>
          Up to 10 questions shown in an accordion on the course detail page. Also generates FAQPage structured data for Google.
        </small>
        {(Array.isArray(cms.faq) ? cms.faq : Array.from({ length: 10 }, () => ({ question: '', answer: '' }))).slice(0, 10).map((f, i) => (
          <div key={i} style={{ marginBottom: '1rem', padding: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.5rem', border: '1px solid var(--border-primary)' }}>
            <small style={{ fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '0.5rem' }}>FAQ {i + 1}</small>
            <Field label="Question">
              <Form.Control
                value={f.question}
                onChange={(e) => setForm((p) => {
                  const faq = [...(p.cms?.faq || [])];
                  faq[i] = { ...faq[i], question: e.target.value };
                  return { ...p, cms: { ...(p.cms || {}), faq } };
                })}
                placeholder="e.g., Do you provide placement support?"
              />
            </Field>
            <Field label="Answer">
              <Form.Control
                as="textarea"
                rows={2}
                value={f.answer}
                onChange={(e) => setForm((p) => {
                  const faq = [...(p.cms?.faq || [])];
                  faq[i] = { ...faq[i], answer: e.target.value };
                  return { ...p, cms: { ...(p.cms || {}), faq } };
                })}
                placeholder="Yes, we offer 100% placement assistance…"
              />
            </Field>
          </div>
        ))}
      </div>

      {/* ── Per-course SEO override (optional) ─────────────────────── */}
      <div className="cms-trainer-block" style={{ marginTop: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
          <h6 className="cms-trainer-block-title" style={{ margin: 0 }}>SEO override (course detail page)</h6>
          {aiAvailable && (
            <AppButton
              variant="secondary"
              size="sm"
              type="button"
              onClick={async () => {
                if (seoAiBusy) return;
                setSeoAiBusy(true);
                try {
                  const r = await aiGenerateSeo({
                    pageType: 'course',
                    courseId: courseId || undefined,
                    context: !courseId ? `Course: ${form.name || form.title || ''}\n${form.description || ''}`.trim() : undefined,
                    regenerate: !!cms.seo?.metaTitle,
                    ...seoAi.payload,
                  });
                  const d = r?.data || {};
                  setForm((p) => ({
                    ...p,
                    cms: {
                      ...(p.cms || {}),
                      seo: {
                        ...(p.cms?.seo || {}),
                        metaTitle: d.metaTitle || p.cms?.seo?.metaTitle || '',
                        metaDescription: d.metaDescription || p.cms?.seo?.metaDescription || '',
                        metaKeywords: Array.isArray(d.keywords) && d.keywords.length
                          ? d.keywords.join(', ') : (p.cms?.seo?.metaKeywords || ''),
                      },
                    },
                  }));
                } catch {
                  /* ignore — user will see the unchanged form */
                } finally { setSeoAiBusy(false); }
              }}
              disabled={seoAiBusy}
            >
              <Sparkles size={14} /> {seoAiBusy ? 'Generating…' : 'Generate with AI'}
            </AppButton>
          )}
        </div>
        <small style={{ display: 'block', marginBottom: '0.75rem', color: 'var(--text-tertiary)' }}>
          Leave blank to fall back to the course title / short description / banner image.
        </small>
        {aiAvailable && (
          <div style={{ maxWidth: 420, marginBottom: '0.9rem' }}>
            <AiModelPicker {...seoAi} />
          </div>
        )}
        <Row className="g-3">
          <Col md={12}>
            <Field label="Meta title" hint="Shown as the browser tab title and in search results (≤ 60 chars)">
              <Form.Control
                value={cms.seo?.metaTitle || ''}
                onChange={(e) => setForm((p) => ({
                  ...p, cms: { ...(p.cms || {}), seo: { ...(p.cms?.seo || {}), metaTitle: e.target.value } },
                }))}
                placeholder="e.g., Java Full Stack Bootcamp — 6-Month Live Training"
                maxLength={70}
              />
            </Field>
          </Col>
          <Col md={12}>
            <Field label="Meta description" hint="Search-result snippet (≤ 160 chars)">
              <Form.Control
                as="textarea"
                rows={2}
                value={cms.seo?.metaDescription || ''}
                onChange={(e) => setForm((p) => ({
                  ...p, cms: { ...(p.cms || {}), seo: { ...(p.cms?.seo || {}), metaDescription: e.target.value } },
                }))}
                placeholder="Industry-grade Java Full Stack training with placement support, live projects and 1-on-1 mentor guidance."
                maxLength={200}
              />
            </Field>
          </Col>
          <Col md={6}>
            <Field label="Keywords (comma-separated)">
              <Form.Control
                value={cms.seo?.metaKeywords || ''}
                onChange={(e) => setForm((p) => ({
                  ...p, cms: { ...(p.cms || {}), seo: { ...(p.cms?.seo || {}), metaKeywords: e.target.value } },
                }))}
                placeholder="java, fullstack, spring boot, react"
              />
            </Field>
          </Col>
          <Col md={6}>
            <Field label="Social share image URL" hint="1200×630 PNG/JPG recommended">
              <Form.Control
                value={cms.seo?.ogImage || ''}
                onChange={(e) => setForm((p) => ({
                  ...p, cms: { ...(p.cms || {}), seo: { ...(p.cms?.seo || {}), ogImage: e.target.value } },
                }))}
                placeholder="https://cdn.example.com/og-java-fullstack.png"
              />
            </Field>
          </Col>
          <Col xs={12}>
            <Form.Check
              type="switch"
              id="course-seo-noindex"
              label="Hide this course from search engines (noindex)"
              checked={!!cms.seo?.noindex}
              onChange={() => setForm((p) => ({
                ...p, cms: { ...(p.cms || {}), seo: { ...(p.cms?.seo || {}), noindex: !p.cms?.seo?.noindex } },
              }))}
            />
          </Col>
        </Row>
      </div>

      {aiAvailable && (
        <AiGenerateModal
          show={aiOpen}
          onHide={() => setAiOpen(false)}
          title="Generate Course Marketing Copy"
          description="AI will draft the marketing fields for this course. Review and edit before saving."
          onGenerate={runAi}
          onApply={applyAi}
          renderPreview={(d) => (
            <div className="cms-ai-preview">
              {d.shortDescription && (
                <p><strong>Short description:</strong> {d.shortDescription}</p>
              )}
              {Array.isArray(d.highlights) && d.highlights.length > 0 && (
                <div>
                  <strong>Highlights:</strong>
                  <ul>{d.highlights.map((h, i) => <li key={i}>{h}</li>)}</ul>
                </div>
              )}
              {Array.isArray(d.whatYouWillLearn) && d.whatYouWillLearn.length > 0 && (
                <div>
                  <strong>Learning outcomes:</strong>
                  <ul>{d.whatYouWillLearn.map((h, i) => <li key={i}>{h}</li>)}</ul>
                </div>
              )}
              {d.trainerBio && <p><strong>Suggested trainer bio:</strong> {d.trainerBio}</p>}
              {Array.isArray(d.faq) && d.faq.length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  <strong>FAQs ({d.faq.length}):</strong>
                  <div style={{ marginTop: '0.5rem' }}>
                    {d.faq.map((f, i) => (
                      <div key={i} style={{ marginBottom: '0.5rem', paddingLeft: '0.75rem', borderLeft: '2px solid var(--border-primary)' }}>
                        <div style={{ fontWeight: 500 }}>{f.question}</div>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{f.answer}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        />
      )}
    </Section>
  );
};

export default AddOrEditCoursePage;
